import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase-config';
import { FiLogOut, FiPlus, FiEdit, FiTrash2, FiImage, FiHelpCircle, FiEye, FiYoutube, FiX, FiLink, FiExternalLink } from 'react-icons/fi';
import logoCaes from '../assets/images/logo.png';
import '../styles/Admin.css';

const AdminPanel = () => {
  const [noticias, setNoticias] = useState([]);
  const [formData, setFormData] = useState({
    titulo: '', resumen: '', categoria: 'Institucional', contenido: '',
    imagen: '', fecha: '', youtubeLink: '', externalLink: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showImagePreview, setShowImagePreview] = useState(false);
  const navigate = useNavigate();

  const noticiasCollectionRef = collection(db, 'noticias');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserEmail(user.email);
      } else {
        navigate('/login');
      }
    });
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    fetchNoticias();
    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, [navigate]);

  const fetchNoticias = async () => {
    const q = query(noticiasCollectionRef, orderBy('timestamp', 'desc'));
    const data = await getDocs(q);
    setNoticias(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imagen || !formData.titulo) {
      alert("El título y la URL de la imagen son obligatorios.");
      return;
    }
    if (!formData.externalLink && !formData.contenido) {
        alert("El campo 'Contenido Completo' es obligatorio para las noticias internas.");
        return;
    }
    setIsLoading(true);
    const finalData = { ...formData, timestamp: Date.now(), fechaPublicacion: serverTimestamp() };
    if (editingId) {
      const noticiaDoc = doc(db, 'noticias', editingId);
      await updateDoc(noticiaDoc, finalData);
    } else {
      await addDoc(noticiasCollectionRef, finalData);
    }
    resetForm();
    await fetchNoticias();
    setIsLoading(false);
  };

  const handleEdit = (noticia) => {
    setEditingId(noticia.id);
    setFormData({
      titulo: noticia.titulo || '',
      resumen: noticia.resumen || '',
      categoria: noticia.categoria || 'Institucional',
      contenido: noticia.contenido || '',
      imagen: noticia.imagen || '',
      fecha: noticia.fecha || '',
      youtubeLink: noticia.youtubeLink || '',
      externalLink: noticia.externalLink || ''
    });
    window.scrollTo(0, 0);
  };

  const handleViewNoticia = (noticia) => {
    if (noticia.externalLink) {
      window.open(noticia.externalLink, '_blank', 'noopener,noreferrer');
    } else {
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}/noticia/${noticia.id}`, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta noticia?")) {
      const noticiaDoc = doc(db, 'noticias', id);
      await deleteDoc(noticiaDoc);
      fetchNoticias();
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      titulo: '', resumen: '', categoria: 'Institucional', contenido: '',
      imagen: '', fecha: '', youtubeLink: '', externalLink: ''
    });
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-header-logo">
          <img src={logoCaes} alt="Logo CAES" className="header-logo" />
          <h2>Editor de Noticias</h2>
        </div>
        <div className="admin-user-info">
            <span><strong>Ingreso:</strong> {userEmail}</span>
            <span>{currentDateTime.toLocaleDateString('es-ES', { dateStyle: 'long' })} - {currentDateTime.toLocaleTimeString('es-ES')}</span>
        </div>
        <button onClick={handleLogout} className="logout-btn">
          <FiLogOut /> <span>Cerrar Sesión</span>
        </button>
      </header>

      <main className="admin-grid-layout">
        <div className="form-container-card">
          <div className="card-header">
            <h3><FiPlus /> {editingId ? 'Editar Noticia' : 'Nueva Noticia'}</h3>
            {editingId && (<button className="btn-clear" onClick={resetForm}>Cancelar Edición</button>)}
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Título</label>
                <input name="titulo" value={formData.titulo} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Resumen</label>
                <textarea name="resumen" value={formData.resumen} onChange={handleInputChange} rows="3" required />
              </div>
              <div className="form-group">
                <label>Contenido Completo (requerido si no es externa)</label>
                <textarea name="contenido" value={formData.contenido} onChange={handleInputChange} rows="6" />
              </div>

              <div className="form-group">
                <div className="label-wrapper">
                  <label>URL de la Imagen</label>
                  <div className="label-actions">
                    {formData.imagen && (
                      <button type="button" className="btn-toggle-preview" onClick={() => setShowImagePreview(true)}>
                        <FiEye /> Vista Previa
                      </button>
                    )}
                    <div className="tutorial-wrapper">
                      <details className="image-tutorial">
                        <summary><FiHelpCircle /></summary>
                        <div>
                          <p>Usa un enlace que termine en <strong>.png, .jpg, etc.</strong></p>
                          <strong>Imgur (Recomendado):</strong>
                          <ol>
                            <li>Ve a <a href="https://imgur.com/upload" target="_blank" rel="noopener noreferrer">imgur.com/upload</a>.</li>
                            <li>Sube tu imagen.</li>
                            <li>Clic derecho sobre la imagen y <strong>"Copiar dirección de la imagen"</strong>.</li>
                            <li>Pega ese enlace aquí.</li>
                          </ol>
                        </div>
                      </details>
                    </div>
                  </div>
                </div>
                <input name="imagen" value={formData.imagen} onChange={handleInputChange} placeholder="Pega el enlace directo de la imagen aquí" required />
              </div>

              <div className="form-group">
                <label><FiLink /> Enlace Externo (Opcional)</label>
                <input name="externalLink" value={formData.externalLink} onChange={handleInputChange} placeholder="https://sitiodeorigen.com/noticia" />
              </div>

              <div className="form-group">
                <label><FiYoutube /> Enlace de YouTube (Opcional)</label>
                <input name="youtubeLink" value={formData.youtubeLink} onChange={handleInputChange} placeholder="Pega aquí el enlace de YouTube" />
              </div>

              <div className="form-group">
                <label>Categoría</label>
                <select name="categoria" value={formData.categoria} onChange={handleInputChange}>
                  <option value="Institucional">Institucional</option>
                  <option value="Mix Empresarial">Mix Empresarial</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-submit" disabled={isLoading}>
                  {isLoading ? 'Guardando...' : (editingId ? 'Actualizar Noticia' : 'Publicar Noticia')}
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="list-container-card">
          <div className="card-header">
            <h3><FiImage /> Noticias Publicadas de CAES</h3>
          </div>
          <div className="card-body list-body">
            {noticias.map((noticia) => (
              <div key={noticia.id} className="noticia-list-item">
                <img src={noticia.imagen} alt={noticia.titulo} className="item-thumbnail" />
                <div className="item-content">
                  <h4>{noticia.titulo}</h4>
                  <div className="item-meta">
                    <span className="item-category">{noticia.categoria}</span>
                    <span className="item-date">{noticia.fechaPublicacion ? new Date(noticia.fechaPublicacion.seconds * 1000).toLocaleDateString('es-ES') : ''}</span>
                    {noticia.externalLink && <span className="item-external"><FiLink size={10}/> Externa</span>}
                  </div>
                </div>
                <div className="item-actions">
                  <button 
                    onClick={() => handleViewNoticia(noticia)} 
                    className="btn-icon btn-view"
                    title={noticia.externalLink ? "Ver noticia externa" : "Ver noticia"}
                  >
                    {noticia.externalLink ? <FiExternalLink /> : <FiEye />}
                  </button>
                  <button onClick={() => handleEdit(noticia)} className="btn-icon btn-edit" title="Editar noticia">
                    <FiEdit />
                  </button>
                  <button onClick={() => handleDelete(noticia.id)} className="btn-icon btn-delete" title="Eliminar noticia">
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {showImagePreview && (
        <div className="image-preview-modal-overlay" onClick={() => setShowImagePreview(false)}>
          <div className="image-preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="btn-close-preview" onClick={() => setShowImagePreview(false)}>
              <FiX />
            </button>
            <img src={formData.imagen} alt="Vista Previa" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;